import { useEffect, useRef } from 'react';

import {
  createUrlWithSourceQueryParams,
  trackSearchClick,
} from '@crehana/analytics';
import { useMe } from '@crehana/auth';
import { Breadcrumb } from '@crehana/compass.knowledgehub/components';
import { cn } from '@crehana/compass.ui/utils';
import { useIntersectionObserver } from '@crehana/react-hooks';
import { useRouter } from 'next/router';

import useRenderContentDetailEventAmplitude from '@/shared/hooks/use-render-content-detail-event-amplitude';

import { EnrollmentsCurrentCourse } from '../components/leader-enrollments-course';
import { useCourseDetailCourse, useCourseDetailTranslation } from '../hooks';
import { useAssignContentLeaderFeature } from '../hooks/use-assign-content-leader-feature';
import CourseLevel from './CourseLevel';
import FloatingCard from './FloatingCard/index';
import Player from './FloatingCard/Player';
import LessonsSection from './Lessons';
import MainSection from './MainSection';

type MainProps = {
  courseDescription: string;
  courseDifficulty?: string;
};

const Main: React.FC<MainProps> = ({ courseDescription, courseDifficulty }) => {
  const { me } = useMe();
  const isB2BUser = !!me?.isB2B;
  const shouldRenderCourseDifficulty = !!courseDifficulty && !isB2BUser;
  const router = useRouter();
  const { t } = useCourseDetailTranslation();
  const { course, organizationSlug } = useCourseDetailCourse();
  const { setEventAmplitud } = useRenderContentDetailEventAmplitude();
  const stickyHeaderRef = useRef<HTMLDivElement>(null);
  const isFromContent = router.query.source_detail === 'Organization Catalog';
  const { isAssignContentLeaderEnabled } = useAssignContentLeaderFeature();

  const observer = useIntersectionObserver(stickyHeaderRef, {
    threshold: 0,
    rootMargin: '0px',
  });

  const isSticky = !(observer?.isIntersecting ?? true);

  useEffect(() => {
    trackSearchClick();
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentionally managed
  useEffect(() => {
    if (course.originalId && course.title) {
      const data = router.query.data as string | undefined;
      setEventAmplitud(data, course.originalId, course.title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course.originalId, course.title]);

  return (
    <main
      className={cn(
        'tw-relative tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-2.4 tw-max-w-screen-md sm:tw-mx-4 md:tw-mx-auto',
        // NOTE: This is a workaround to avoid the sticky header to be hidden/shown infinite times
        isAssignContentLeaderEnabled && 'tw-min-h-[120svh]',
      )}
    >
      <div className="md:tw-col-span-2">
        <Player className="tw-bg-neutral-light-800 md:tw-hidden tw-mb-2" />

        {isFromContent && (
          <Breadcrumb
            className="tw-max-w-screen-md tw-mx-auto"
            items={[
              {
                label: t('KNOWLEDGEHUB_HOME'),
                onClick: () => {
                  const urlContent =
                    '/org/' + me?.organization?.slug + '/learn/content/';

                  const url = createUrlWithSourceQueryParams({
                    source: 'Content Detail',
                    url: urlContent,
                  });

                  window.location.href = url;
                },
              },
              {
                label: t('KNOWLEDGEHUB_DETAIL'),
              },
            ]}
            classNameItem="tw-text-primary-light-500 dark:tw-text-neutral-light-100"
          />
        )}

        <div ref={stickyHeaderRef} className="tw-h-0.1" />

        <MainSection
          courseDescription={courseDescription}
          isFromContent={isFromContent}
        />

        <div className="tw-flex tw-items-center tw-justify-center md:tw-justify-start">
          <div className="tw-w-full tw-px-1.6 md:tw-px-0 tw-max-w-[74rem]">
            <LessonsSection />
            {shouldRenderCourseDifficulty && (
              <CourseLevel courseLevel={courseDifficulty} />
            )}
          </div>
        </div>
      </div>

      <div className="tw-col-span-1 tw-hidden md:tw-block">
        <div className="tw-sticky tw-top-2.4 tw-w-full tw-max-w-screen-xs">
          <FloatingCard isSticky={isSticky} />

          {isAssignContentLeaderEnabled && (
            <EnrollmentsCurrentCourse
              courseTitle={course.title}
              courseId={course.originalId}
              organizationSlug={organizationSlug}
              className="tw-mt-2.4"
            />
          )}
        </div>
      </div>
    </main>
  );
};

export default Main;
